import React, { Component } from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ensureListing } from '../../../util/data';
import { AVAILABILITY_FIELD_KEY, getAvailabilityBadgeProps } from '../../../util/availability';

import css from './SearchMapGroupLabel.module.css';

// Group markers represent multiple listings sharing the same coordinates.
// Rule: green if at least one listing in the group is available now, red if
// none are, neutral (default) if availability isn't set on any of them.
const getGroupAvailability = (listings, config) => {
  const statuses = listings.map(
    listing => getAvailabilityBadgeProps(ensureListing(listing).attributes.publicData, config)?.isAvailable
  );
  if (statuses.some(isAvailable => isAvailable === true)) {
    return true;
  }
  if (statuses.some(isAvailable => isAvailable === false)) {
    return false;
  }
  return null;
};

/**
 * SearchMapGroupLabel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {Function} props.onListingClicked - The function to handle the listing click
 * @param {boolean} [props.isActive] - The active status
 * @param {string} [props.mapComponentRefreshToken] - The map component refresh token
 * @param {Object} [props.config] - The configuration, used to resolve each listing's availability status
 * @returns {JSX.Element}
 */
class SearchMapGroupLabel extends Component {
  shouldComponentUpdate(nextProps) {
    const hasSameAmountOfListings = nextProps.listings.length === this.props.listings.length;
    const hasSameActiveStatus = this.props.isActive === nextProps.isActive;
    const hasSameRefreshToken =
      this.props.mapComponentRefreshToken === nextProps.mapComponentRefreshToken;
    const availabilitySignature = listings =>
      listings.map(l => ensureListing(l).attributes.publicData?.[AVAILABILITY_FIELD_KEY]).join(',');
    const hasSameAvailability =
      availabilitySignature(this.props.listings) === availabilitySignature(nextProps.listings);

    return !(
      hasSameAmountOfListings &&
      hasSameActiveStatus &&
      hasSameRefreshToken &&
      hasSameAvailability
    );
  }

  render() {
    const { className, rootClassName, listings, onListingClicked, isActive, config } = this.props;
    const classes = classNames(rootClassName || css.root, className);
    const groupIsAvailable = getGroupAvailability(listings, config);
    const countLabelClasses = classNames(css.details, {
      [css.detailsActive]: isActive,
      [css.detailsAvailable]: !isActive && groupIsAvailable === true,
      [css.detailsUnavailable]: !isActive && groupIsAvailable === false,
    });
    const caretClasses = classNames(css.caret, {
      [css.caretActive]: isActive,
      [css.caretAvailable]: !isActive && groupIsAvailable === true,
      [css.caretUnavailable]: !isActive && groupIsAvailable === false,
    });

    return (
      <button className={classes} onClick={() => onListingClicked(listings)}>
        <div className={css.caretShadow} />
        <div className={countLabelClasses}>{listings.length}</div>
        <div className={caretClasses} />
      </button>
    );
  }
}

export default SearchMapGroupLabel;
